import type { SecurityPolicyDraft } from '@/services/business/security/policy';
import type { SmsProtection } from '@/services/business/security/smsProtection';
import type {
  AccessIpRule,
  AutoProtectionRule,
  LoginPolicyValues,
  SecurityEvent,
  SecuritySession,
} from '@/services/business/security/types';
import { createRandomId } from '@/utils/randomId';

export const defaultLoginCaptchaConfig = {
  maxFailures: 5,
  lockMinutes: 15,
  humanVerification: { mode: 'afterFailures', failureThreshold: 3 },
} satisfies Omit<LoginPolicyValues, 'security'>;

export const defaultAutoProtection: AutoProtectionRule[] = [
  {
    kind: 'loginSpray',
    enabled: true,
    windowMinutes: 5,
    threshold: 20,
    durationMinutes: 15,
    action: 'blockIp',
    ipBan: { strategy: 'fixed', secondHours: 1, subsequentHours: 24, resetHours: 24 },
  },
  {
    kind: 'verificationFlood',
    enabled: true,
    windowMinutes: 10,
    threshold: 10,
    durationMinutes: 10,
    action: 'pauseVerification',
  },
  {
    kind: 'apiFlood',
    enabled: true,
    windowMinutes: 1,
    threshold: 300,
    durationMinutes: 5,
    action: 'throttleApi',
  },
];
export const defaultSmsProtection: SmsProtection = {
  phoneCooldownSeconds: 60,
  phonePer10Minutes: 3,
  phonePer24Hours: 10,
  ipPer10Minutes: 30,
  accountPer24Hours: 100,
  globalPer24Hours: 500,
};
export const defaultSecurityPolicy: SecurityPolicyDraft = {
  mfaRequirement: 'admins',
  riskTriggers: ['newDevice', 'unusualLocation'],
  riskAction: 'challenge',
  deliveryMethods: ['email', 'sms'],
  codeMinutes: 5,
  resendSeconds: 60,
  maxCodeAttempts: 5,
};

export function seedSecurity(): {
  events: SecurityEvent[];
  sessions: SecuritySession[];
  autoProtection: AutoProtectionRule[];
  smsProtection: SmsProtection;
  allowedIps: AccessIpRule[];
  deniedIps: AccessIpRule[];
} {
  const now = Date.now(),
    at = (minutes: number) => new Date(now - minutes * 60000).toISOString();
  const events: SecurityEvent[] = Array.from({ length: 12 }, (_, i) => ({
    id: `SEC2026101100${String(i + 1).padStart(4, '0')}`,
    type: (['failedLogins', 'newDevice', 'blockedIp', 'accountCompromised'] as const)[
      i % 4
    ],
    account: ['admin', 'operator', 'auditor'][i % 3],
    ip: `192.0.2.${20 + i}`,
    severity: i % 3 === 0 ? 'critical' : 'warning',
    status: i < 8 ? 'open' : 'resolved',
    createdAt: at(i * 20 + 1),
    processedAt: i < 8 ? null : at(i),
    note: i < 8 ? '' : 'Reviewed',
    resolution: i < 8 ? null : 'mitigated',
    processedBy: i < 8 ? null : { id: '00000001', name: 'admin' },
    handlingRecords:
      i < 8
        ? []
        : [
            {
              resolution: 'mitigated',
              actor: { id: '00000001', name: 'admin' },
              note: 'Reviewed',
              at: at(i),
            },
          ],
  }));
  events.unshift({
    id: 'SEC20261011000099',
    type: 'autoProtection',
    account: 'admin',
    ip: '203.0.113.90',
    severity: 'warning',
    status: 'open',
    createdAt: at(1),
    processedAt: null,
    note: '',
    resolution: null,
    processedBy: null,
    handlingRecords: [],
    restriction: {
      kind: 'loginSpray',
      action: 'blockIp',
      target: '203.0.113.90',
      count: 23,
      threshold: 20,
      windowMinutes: 5,
      startedAt: at(1),
      expiresAt: new Date(now + 14 * 60000).toISOString(),
      status: 'active',
      releasedAt: null,
      releasedBy: null,
      releaseNote: '',
    },
  });
  for (const [index, status] of (['expired', 'released'] as const).entries()) {
    const event = structuredClone(events[0]);
    event.id = `SEC2026101100010${index}`;
    event.ip = `203.0.113.${91 + index}`;
    if (event.restriction) {
      event.restriction.target = event.ip;
      event.restriction.status = status;
      event.restriction.startedAt = at(90);
      event.restriction.expiresAt = at(75);
      if (status === 'released') {
        event.restriction.releasedAt = at(80);
        event.restriction.releasedBy = { id: '00000001', name: 'admin' };
        event.restriction.releaseNote = 'Investigation completed';
      }
    }
    events.push(event);
  }
  return {
    events,
    sessions: Array.from({ length: 8 }, (_, i) => ({
      id: createRandomId(),
      account: ['admin', 'operator', 'auditor'][i % 3],
      device: ['Chrome · Windows 11', 'Safari · iOS', 'Edge · Windows 11'][i % 3],
      ip: `198.51.100.${10 + i}`,
      current: i === 0,
      status: i < 6 ? 'active' : i === 6 ? 'revoked' : 'expired',
      createdAt: at(i + 90),
      lastActiveAt: at(i),
      endedAt: i < 6 ? null : at(i),
      note: '',
    })),
    autoProtection: structuredClone(defaultAutoProtection),
    smsProtection: structuredClone(defaultSmsProtection),
    allowedIps: [
      {
        id: 'ALLOW-1',
        address: '10.10.0.0/16',
        note: 'Office network',
        enabled: true,
        source: 'manual',
        expiresAt: null,
      },
      {
        id: 'ALLOW-V6',
        address: '2001:db8::/32',
        note: 'Operations VPN',
        enabled: true,
        source: 'manual',
        expiresAt: null,
      },
    ],
    deniedIps: [
      {
        id: 'DENY-1',
        address: '192.0.2.200',
        note: 'Repeated login attempts',
        enabled: true,
        source: 'manual',
        expiresAt: null,
      },
      {
        id: 'DENY-AUTO-1',
        address: '203.0.113.90',
        note: '',
        enabled: true,
        source: 'automatic',
        expiresAt: events[0].restriction?.expiresAt ?? null,
        eventId: events[0].id,
      },
    ],
  };
}
